import { Button, Icon, InputItem, Tooltip } from "pepsico-ds";
import PropTypes from "prop-types";
import { useState } from "react";
import LimitTypeSelectorModal from "./LimitTypeSelectorModal";
import WinLimitsFields from "./WinLimitsFields";
import { DEFAULT_INVENTORY_LIMIT_TYPES, DEFAULT_REWARD_FIELDS } from "./constants";

const RewardItemConfig = ({
  reward,
  index,
  distributionStrategy,
  onChange,
  onRemove,
  disabled = false,
  showDeleteReward = true,
  inventoryLimitTypes,
  winLimitTypes,
  rewardFields,
  showConfigurationSection = true,
  showMaximumAllocationsSection = true,
  showMaximumAllocationsPerUserSection = true,
  setAutoPriority = false,
  inventoryLimitTitle = "Maximum Allocations",
  inventoryLimitDescription = "Maximum number of times this reward can be granted within the selected timeframe, regardless of the user.",
  winLimitTitle = "Maximum Allocations per User",
  winLimitDescription = "Maximum number of times this reward can be granted to a user within the selected timeframe",
}) => {
  // Use provided definitions or fall back to defaults
  const inventoryLimits =
    inventoryLimitTypes && inventoryLimitTypes.length > 0
      ? inventoryLimitTypes
      : DEFAULT_INVENTORY_LIMIT_TYPES;
  const fields =
    rewardFields && rewardFields.length > 0 ? rewardFields : DEFAULT_REWARD_FIELDS;
  const [showInventoryLimitModal, setShowInventoryLimitModal] = useState(false);
  const preventNumberScroll = (e) => {
    e.currentTarget.blur();
  };

  // Normalize status if it's an array/object (from Dropdown)
  const normalizedReward = {
    ...reward,
    status: "ACTIVE",
  };

  const handleFieldChange = (field, value) => {
    onChange(index, { ...normalizedReward, [field]: value, status: "ACTIVE" });
  };

  const handleIntChange = (field, value) => {
    if (value === "") {
      handleFieldChange(field, "");
      return;
    }
    const parsed = parseInt(value, 10);
    handleFieldChange(field, isNaN(parsed) ? "" : parsed);
  };

  const handleUserWinLimitsChange = (newLimits) => {
    onChange(index, { ...normalizedReward, user_win_limits: newLimits });
  };

  const handleAddInventoryLimit = (limitType) => {
    handleFieldChange(limitType.key, "");
  };

  const handleRemoveInventoryLimit = (limitKey) => {
    handleFieldChange(limitKey, undefined);
  };

  const activeInventoryLimits = inventoryLimits.filter((limit) => {
    const value = normalizedReward[limit.key];
    // Show if: required OR value is a number OR empty string (just added)
    return limit.required || typeof value === "number" || value === "";
  });

  const availableInventoryLimits = inventoryLimits.filter((limit) => {
    const value = normalizedReward[limit.key];
    // Available if null or undefined AND not required
    return (value === null || value === undefined) && !limit.required;
  });

  return (
    <details className="reward-accordion" open>
      <summary className="reward-accordion-summary">
        <div className="display-flex justify-content-space-between align-items-center w-100">
          <div
            className="display-flex align-items-center"
            style={{ gap: "12px", flex: 1 }}
          >
            <span className="font-semibold">
              {normalizedReward.reward_name ||
                normalizedReward.reward_id ||
                "Reward"}
            </span>
          </div>
          {!disabled && showDeleteReward && onRemove && (
            <Tooltip text="Remove reward" position="top">
              <button
                className="icon-button delete-button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onRemove(index);
                }}
                type="button"
              >
                <Icon icon="delete" size="small" />
              </button>
            </Tooltip>
          )}
        </div>
      </summary>
      <div className="reward-accordion-content">
        {/* Core Reward Fields */}
        {showConfigurationSection && (
          <>
            <div className="form-row">
              {distributionStrategy === "ORDER_BASED" && !setAutoPriority && (
                <div className="form-col">
                  <div className="form-field-wrapper">
                    <label
                      className={`form-field-label ${fields.find((f) => f.key === "priority_order")?.required_when ? "required" : ""}`}
                    >
                      {fields.find((f) => f.key === "priority_order")?.label ||
                        "Priority Order"}
                    </label>
                    <InputItem
                      type="number"
                      size="medium"
                      min="0"
                      value={normalizedReward.priority_order ?? ""}
                      onChange={(e) =>
                        handleIntChange("priority_order", e.target.value)
                      }
                      onWheel={preventNumberScroll}
                      disabled={disabled}
                    />
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Per-Reward Inventory Limits */}
        {showMaximumAllocationsSection && (
          <div className="mt-4">
            <div className="display-flex justify-content-space-between align-items-center mb-3">
              <div>
                <p className="font-semibold">{inventoryLimitTitle}</p>
                <span className="font-xs">{inventoryLimitDescription}</span>
              </div>

              {!disabled && availableInventoryLimits.length > 0 && (
                <Button
                  text="Add Limit"
                  iconLeading="add"
                  variant="tertiary"
                  size="small"
                  onClick={() => setShowInventoryLimitModal(true)}
                />
              )}
            </div>
            <div className="form-row form-row-thirds">
              {activeInventoryLimits.map((limit) => (
                <div className="form-col-third" key={limit.key}>
                  <div className="form-field-wrapper">
                    <label className="form-field-label">{limit.label}</label>

                    <div className="input-with-delete">
                      <div className="input-wrapper">
                        <InputItem
                          type="number"
                          size="medium"
                          min="0"
                          value={normalizedReward[limit.key] ?? ""}
                          onChange={(e) =>
                            handleIntChange(limit.key, e.target.value)
                          }
                          onWheel={preventNumberScroll}
                          disabled={disabled}
                        />
                      </div>
                      {!disabled && !limit.required && (
                        <Tooltip text="Remove limit" position="top">
                          <button
                            className="icon-button delete-button"
                            onClick={() => handleRemoveInventoryLimit(limit.key)}
                            type="button"
                          >
                            <Icon icon="delete" size="small" />
                          </button>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Per-Reward User Win Limits */}
        {showMaximumAllocationsPerUserSection && (
          <div className="mt-3">
            <WinLimitsFields
              title={winLimitTitle}
              subtitle={winLimitDescription}
              limits={normalizedReward.user_win_limits || {}}
              onChange={handleUserWinLimitsChange}
              disabled={disabled}
              limitTypes={winLimitTypes}
            />
          </div>
        )}
      </div>

      <LimitTypeSelectorModal
        show={showInventoryLimitModal}
        onClose={() => setShowInventoryLimitModal(false)}
        limitTypes={availableInventoryLimits}
        onSelectLimit={handleAddInventoryLimit}
        title="Add Inventory Limit"
      />
    </details>
  );
};

RewardItemConfig.propTypes = {
  reward: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  distributionStrategy: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
  disabled: PropTypes.bool,
  showDeleteReward: PropTypes.bool,
  inventoryLimitTypes: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      description: PropTypes.string,
    })
  ),
  winLimitTypes: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      description: PropTypes.string,
    })
  ),
  rewardFields: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string,
      required: PropTypes.bool,
      readonly: PropTypes.bool,
      field_type: PropTypes.string,
      required_when: PropTypes.object,
      description: PropTypes.string,
    })
  ),
  showConfigurationSection: PropTypes.bool,
  showMaximumAllocationsSection: PropTypes.bool,
  showMaximumAllocationsPerUserSection: PropTypes.bool,
  setAutoPriority: PropTypes.bool,
  inventoryLimitTitle: PropTypes.string,
  inventoryLimitDescription: PropTypes.string,
  winLimitTitle: PropTypes.string,
  winLimitDescription: PropTypes.string,
};

export default RewardItemConfig;
